import React from 'react';
import { useIntl } from 'react-intl-next';

import type { ClassificationOption } from '@atlassian/data-classification-level';
import { EditPopup } from '@atlassian/data-classification-level';

import { fg } from '@confluence/feature-gating';

import type { ClassificationLevel } from '../ClassificationRadioOption';
import { useDefaultAndSpaceClassificationLevels } from '../hooks/useDefaultAndSpaceClassificationLevels';
import { getAllClassificationLevels } from '../utils/getAllClassificationLevels';
import { useComputeDefaultClassificationDropdownOption } from '../hooks/useComputeDefaultClassificationDropdownOption';

import { sharedMessages } from './sharedMessages';

type EditViewProps = {
	spaceKey: string;
	defaultOption?: ClassificationOption; // TODO: remove this once fg('dlp_cc-minimize-classifications-queries') is removed
	onEditCancel: () => void;
	onEditSubmit: (classification: ClassificationOption) => void;
	selectedOption?: ClassificationOption;
	loading?: boolean;
	hasError?: boolean;
};

export const ClassificationEditViewPopup = ({
	spaceKey,
	selectedOption: selectedOptionProp,
	defaultOption: defaultOptionProp,
	onEditCancel,
	onEditSubmit,
	loading = false,
}: EditViewProps) => {
	const { formatMessage } = useIntl();

	const {
		defaultClassification: spaceDefaultClassification,
		classificationLevels,
		getClassificationLevelFromId,
		expectedError,
		loading: spaceAndDefaultLoading,
	} = useDefaultAndSpaceClassificationLevels({ spaceKey });

	const {
		defaultClassificationOption: defaultClassificationOptionNew,
		error: defaultClassificationError,
		loading: orgDefaultLoading,
	} = useComputeDefaultClassificationDropdownOption({
		spaceDefaultClassificationLevel: spaceDefaultClassification,
		getClassificationLevelFromId,
		skipQuery: !fg('dlp_cc-minimize-classifications-queries'), // default will come from prop otherwise
	});

	const defaultOption = fg('dlp_cc-minimize-classifications-queries')
		? defaultClassificationOptionNew
		: defaultOptionProp;

	const selectedOption = fg('dlp_cc-minimize-classifications-queries')
		? selectedOptionProp || defaultOption
		: selectedOptionProp;

	let allClassificationLevels: ClassificationLevel[] = [
		...getAllClassificationLevels(classificationLevels),
	];
	// NOTE: Realistically, in either flag state it should not be undefined —
	// Remove this typehack with when fg('dlp_cc-minimize-classifications-queries') removed
	if (defaultOption) {
		allClassificationLevels = [...allClassificationLevels, defaultOption];
	}

	const hasError = !!expectedError || !!defaultClassificationError;

	// Note: If we do not re-render the EditPopup after loading is completed,
	// the selectedOption may not be correct, as its only used in EditPopup
	// for the initial useState value; it is not a controlled value.
	const combinedLoadingCondition = loading || spaceAndDefaultLoading || orgDefaultLoading;
	if (combinedLoadingCondition && fg('dlp_cc-minimize-classifications-queries')) {
		return null;
	}

	return (
		<EditPopup
			options={allClassificationLevels}
			selectedOption={selectedOption}
			onEditCancel={onEditCancel}
			onEditSubmit={onEditSubmit}
			loading={combinedLoadingCondition}
			errorMessage={hasError ? formatMessage(sharedMessages.fetchErrorTextPrimary) : undefined}
		/>
	);
};
